import { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';

// Build stamp surfaced in console so dogfood can verify which bundle is live.
// Bumped when the Report 渲染 path is changed; works around CDN/browser cache
// holding stale JS chunks past index.html revalidation.
const REPORT_BUILD = 'fyd-report-v43-report-pending';

// Auto-retry budget for the `pending` page-state: 12 × 5s ≈ 60s. Beyond this
// we stop the polling loop and show a "似乎卡住了" copy with a manual retry
// CTA — without ever claiming the council aborted or a refund was issued.
const PENDING_MAX_ATTEMPTS = 12;
const PENDING_INTERVAL_MS = 5000;
// eslint-disable-next-line no-console
console.log('[Report] build', REPORT_BUILD);
import { reportGetJson, reportDownloadMd, sessionGetDetail } from '@/api/m0_sessions';
import type { SceneSymbolId } from '@/api/m0_sessions';
import AbortedSessionBanner from '@/components/AbortedSessionBanner';
import {
  selectCouncilHealth,
  selectQualityCompromised,
  selectRefund,
  selectAttachmentCitationsTooFew,
} from './report_selectors';
import { useFydUser } from '@/store/auth';
import PageHeader from '@/ui/organisms/PageHeader';
import Eyebrow from '@/ui/atoms/Eyebrow';
import Button from '@/ui/atoms/Button';
import LoadingState from '@/ui/molecules/LoadingState';
import ErrorState from '@/ui/molecules/ErrorState';
import EmptyState from '@/ui/molecules/EmptyState';
import SubscribeNudge from '@/ui/molecules/SubscribeNudge';
import { FirstWinBanner } from '@/components/FirstWinBanner';
import { NextActionCta } from '@/components/NextActionCta';
import { useAhaFlag } from '@/hooks/useAhaFlag';
import reportBrewingSvg from '@/assets/empty/report_brewing.svg';

// /sessions/:id/report — renders the BE M0 types.Report 5-section payload:
//   1. current_position  (where the user stands today)
//   2. candidates        (3-5 candidates with pros/cons/validation/4w milestone/kill criteria)
//   3. dissent           (Iron's standing objections per candidate)
//   4. ranking           (numbered ranking with rationale)
//   5. first_action      (single concrete next step)
//   + audit              (informational provenance trailer)
//
// Spec: docs/contracts/m0/openapi.fyd.v1.yaml session_get_report; backend
// internal/types/report.go is the source-of-truth shape.

interface ReportM0 {
  report_id: string;
  session_id: string;
  user_id?: string;
  resolution_type?: string;
  generated_at: string;
  renderer_version?: string;

  current_position: { text: string };
  candidates: Array<{
    candidate_id: string;
    letter: string;
    title: string;
    one_liner?: string;
    pros?: string[];
    cons?: string[];
    validation_steps?: string[];
    four_week_milestone?: string;
    kill_criteria?: string;
    agents_leaning?: string[];
  }>;
  dissent: {
    quality_compromised: boolean;
    standing_objections: Array<{
      critic_alias: string;
      critic_id: string;
      opposed_candidate_letter: string;
      quote: string;
    }>;
  };
  // BE wire schema (internal/types/report.go): RankingEntry uses
  // candidate_letter + reason. Aliases kept for forward-compat if BE ever
  // adds them.
  ranking: Array<{
    rank: number;
    candidate_letter: string;
    reason: string;
    // legacy/forward-compat aliases — not on the current wire shape.
    candidate_id?: string;
    rationale?: string;
  }>;
  // FirstAction wire shape is {text}. Legacy fields kept optional for
  // forward-compat.
  first_action: {
    text?: string;
    one_liner?: string;
    candidate_id?: string;
    deadline?: string;
    success_criterion?: string;
  };
  audit?: {
    rule_1_satisfied?: boolean;
    rule_2_satisfied?: boolean;
    rule_3_satisfied?: boolean;
    candidates_count_in_range?: boolean;
    first_action_is_single?: boolean;
    forbidden_phrases_found?: string[];
    parser_fallbacks_count?: number;
  };
}

type Status =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; httpStatus?: number; errorCode?: string; traceId?: string; sessionStatus?: string }
  | { kind: 'ready'; report: ReportM0 }
  // iter_5: aborted state replaces generic 'empty' when session is known aborted.
  | { kind: 'aborted'; abortReason: string | null }
  // FYD-2026-0512 — `pending` is what we used to mislabel `session_incomplete`:
  // /reports/{id} 404 + session still 'running' (no completed_at / report_id).
  // This is *not* an abort — most often it's a freshly created session whose
  // sage_synthesis hasn't called Save() yet. Auto-poll until the report shows
  // up or we hit PENDING_MAX_ATTEMPTS. Never claims a refund.
  | { kind: 'pending'; attempt: number };

interface AuditCheckProps {
  label: string;
  ok: boolean | undefined;
}

function AuditCheck({ label, ok }: AuditCheckProps) {
  const status = ok === true ? '✓' : ok === false ? '✗' : '—';
  const tone = ok === true ? 'text-jade-700' : ok === false ? 'text-vermilion-700' : 'text-ink-3';
  return (
    <div className="flex items-baseline gap-3 px-3 py-2 border border-hairline rounded">
      <span className={`font-mono text-base ${tone}`}>{status}</span>
      <span className="text-ink-2 leading-tight">{label}</span>
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useFydUser((s) => s.me);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  // ★ Hooks MUST run unconditionally (React rules of hooks · #310). Hoist
  // useAhaFlag here from its previous post-early-return position so all 5
  // early returns (loading/aborted/empty/error + ready) keep the same hook
  // invocation order. Was the root cause of the "白屏 → 渲染异常" dogfood bug.
  const progressFlag = useAhaFlag('FYD_AHA_PROGRESS_REVEAL');

  const showSubscribeNudge =
    !!me && me.trial_used && me.subscription_status === 'inactive';

  // `silent=true` skips the loading skeleton flash — used by the pending
  // page-state's auto-retry loop so the UI doesn't strobe between
  // "pending" and "loading" every 5s.
  const load = (silent = false) => {
    if (!id) return;
    if (!silent) setStatus({ kind: 'loading' });
    reportGetJson<ReportM0>(id)
      .then((report) => {
        // eslint-disable-next-line no-console
        console.log('[Report] fetched', {
          session_id: id,
          has_report: !!report,
          report_typeof: typeof report,
          candidates_count: Array.isArray(report?.candidates) ? report.candidates.length : 'not_array',
          dissent_objections: Array.isArray(report?.dissent?.standing_objections)
            ? report.dissent.standing_objections.length : 'not_array',
          ranking_count: Array.isArray(report?.ranking) ? report.ranking.length : 'not_array',
          first_action_present: !!report?.first_action,
          generated_at: report?.generated_at,
        });
        // M2 fix · always render whatever BE returned. Removing the
        // "fail-soft to empty" branch — it masked real bugs (saw "尚未就绪"
        // even when BE confirmed report_fetch_ok in fly logs). If the
        // payload is non-object (network/CDN error returned HTML), surface
        // it as a real error with diagnostic.
        if (!report || typeof report !== 'object') {
          // eslint-disable-next-line no-console
          console.error('[Report] non-object response', { report, type: typeof report });
          setStatus({
            kind: 'error',
            message: `服务器返回了非预期的响应类型: ${typeof report}`,
            errorCode: 'response_not_object',
          });
          return;
        }
        setStatus({ kind: 'ready', report });
      })
      .catch((err: Error) => {
        const apiErr = err instanceof ApiError ? err : null;
        // eslint-disable-next-line no-console
        console.error('[Report] fetch failed', {
          session_id: id,
          name: err?.name,
          message: err?.message,
          http_status: apiErr?.status,
          error_code: apiErr?.errorCode,
          trace_id: apiErr?.traceId,
        });
        // Probe session status so we can show the right banner.
        sessionGetDetail(id)
          .then((d) => {
            const detail = d as {
              status?: string;
              abort_reason?: string | null;
              completed_at?: string | null;
              report_id?: string | null;
            };
            // eslint-disable-next-line no-console
            console.log('[Report] session probe', detail);
            if (detail.status === 'aborted') {
              setStatus({ kind: 'aborted', abortReason: detail.abort_reason ?? null });
              return;
            }
            // ★ FYD-2026-0512 · `pending` (was: aborted/session_incomplete).
            // /reports/{id} 404 + session row exists with status !== 'aborted'
            // AND no completed_at / report_id means the session is still
            // 'running' (only status left in the union) — sage_synthesis has
            // not yet called Save(). Previously we mislabeled this as
            // "Council aborted · refund issued", which was wrong on both
            // counts: the council is probably still alive, and no refund was
            // ever issued. Now: switch to the `pending` page-state and let
            // the auto-retry useEffect poll until the report shows up.
            // (The earlier 2026-05-10 llm_5xx_prolonged case the old comment
            // referenced will surface as a real status='aborted' once the
            // owner finishes its abort path — that hits the branch above.)
            const is404SessionMissing =
              apiErr?.status === 404 &&
              (apiErr.errorCode === 'session_not_found' || apiErr.errorCode === 'not_found');
            const sessionNotCompleted =
              !detail.completed_at && !detail.report_id && detail.status !== 'completed';
            if (is404SessionMissing && sessionNotCompleted) {
              setStatus((prev) => ({
                kind: 'pending',
                attempt: prev.kind === 'pending' ? prev.attempt + 1 : 1,
              }));
              return;
            }
            // Surface the original report fetch error with full diagnostic
            // so the user/dogfood can see WHY it failed. Previously this
            // path dumped to empty ("尚未就绪") which hid the root cause.
            setStatus({
              kind: 'error',
              message: err?.message ?? 'unknown',
              httpStatus: apiErr?.status,
              errorCode: apiErr?.errorCode,
              traceId: apiErr?.traceId,
              sessionStatus: detail.status,
            });
          })
          .catch((probeErr: Error) => {
            const probeApi = probeErr instanceof ApiError ? probeErr : null;
            // eslint-disable-next-line no-console
            console.error('[Report] session probe failed', {
              http_status: probeApi?.status,
              error_code: probeApi?.errorCode,
              message: probeErr?.message,
            });
            // ★ Robustness: both /reports/{id} AND /sessions/{id} return 404
            // = session truly gone (e.g. fly redeploy wiped in-memory + no
            // snapshot resume). Show aborted banner with safer copy than raw
            // double-404 dump.
            if (apiErr?.status === 404 && probeApi?.status === 404) {
              setStatus({ kind: 'aborted', abortReason: 'session_lost' });
              return;
            }
            setStatus({
              kind: 'error',
              message: err?.message ?? probeErr?.message ?? 'unknown',
              httpStatus: apiErr?.status ?? probeApi?.status,
              errorCode: apiErr?.errorCode ?? probeApi?.errorCode,
              traceId: apiErr?.traceId ?? probeApi?.traceId,
            });
          });
      });
  };

  useEffect(load, [id]);

  // Auto-retry while in the `pending` state. Each tick re-fetches /reports/{id}
  // silently (no loading skeleton). The fetch result drives the next state:
  //   - report ready → kind='ready' (exit)
  //   - report still 404 + session still running → kind='pending' (attempt+1)
  //   - anything else → kind='error'|'aborted' (exit)
  // Stops once attempt ≥ PENDING_MAX_ATTEMPTS so we don't poll forever; the
  // render branch then shows a "似乎卡住了" copy with a manual retry CTA.
  useEffect(() => {
    if (status.kind !== 'pending') return;
    if (status.attempt >= PENDING_MAX_ATTEMPTS) return;
    const handle = window.setTimeout(() => load(true), PENDING_INTERVAL_MS);
    return () => window.clearTimeout(handle);
  }, [status]);

  const onDownloadMd = async () => {
    if (!id) return;
    try {
      const blob = await reportDownloadMd(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fyd-report-${id}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('download md failed', err);
    }
  };

  if (!id) return <Navigate to="/dashboard" replace />;

  if (status.kind === 'loading') {
    return (
      <div className="max-w-3xl mx-auto px-8 py-12">
        <LoadingState variant="skeleton" rows={3} label={t('report.loading')} />
      </div>
    );
  }
  if (status.kind === 'aborted') {
    // iter_5: P2 empty_state_seduction error_aborted category.
    return (
      <div className="max-w-3xl mx-auto px-8 py-16">
        <AbortedSessionBanner abortReason={status.abortReason} sessionId={id} />
      </div>
    );
  }
  if (status.kind === 'pending') {
    const exhausted = status.attempt >= PENDING_MAX_ATTEMPTS;
    return (
      <div
        className="max-w-3xl mx-auto px-8 py-16"
        data-testid="report-pending"
        data-state={exhausted ? 'exhausted' : 'polling'}
        data-attempt={status.attempt}
      >
        <EmptyState
          eyebrow={
            exhausted
              ? t('report.pending.giveUpEyebrow', { defaultValue: '议会进展缓慢' })
              : t('report.pending.eyebrow', { defaultValue: '报告生成中' })
          }
          title={
            exhausted
              ? t('report.pending.giveUpTitle', { defaultValue: '暂时拿不到这份报告。' })
              : t('report.pending.title', { defaultValue: '议会正在收敛 · 报告即将就绪。' })
          }
          sub={
            exhausted
              ? t('report.pending.giveUpBody', {
                  defaultValue:
                    '请稍后从 dashboard 重新检查 · 如已扣费，款项会在议会确认结束后按状态自动处理。',
                })
              : `${t('report.pending.body', {
                  defaultValue: '我们会自动重试 · 无需刷新页面。',
                })} ${t('report.pending.attempt', {
                  n: status.attempt,
                  max: PENDING_MAX_ATTEMPTS,
                  defaultValue: `(自动重试 ${status.attempt}/${PENDING_MAX_ATTEMPTS})`,
                })}`
          }
          illustrationSrc={reportBrewingSvg}
          illustrationAlt={t('report.pending.title', {
            defaultValue: '议会正在收敛 · 报告即将就绪。',
          })}
          action={
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                type="button"
                onClick={() => load(false)}
                className="btn btn-primary"
                data-testid="report-pending-manual-retry"
              >
                {t('report.pending.manualRetry', { defaultValue: '立即重试' })}
              </button>
              <Link to="/dashboard" className="btn btn-secondary">
                {t('common.back')}
              </Link>
            </div>
          }
        />
      </div>
    );
  }
  if (status.kind === 'error') {
    return (
      <div className="max-w-3xl mx-auto px-8 py-16">
        <ErrorState
          title={t('report.errorTitle')}
          message={status.message}
          retry={load}
          retryLabel={t('common.retry')}
        />
        {/* Diagnostic block surfacing exactly what went wrong (FYD M2 dogfood
            instrumentation). Keeps developer trace next to the user-facing
            error so the user can paste it back to triage the bug. */}
        <details open className="mt-6 px-4 py-3 bg-paper-2 rounded text-xs font-mono text-ink-2">
          <summary className="cursor-pointer text-ink mb-2">技术细节 (Technical detail)</summary>
          <dl className="grid grid-cols-1 gap-1 mt-2">
            <div><span className="text-ink-3">session_id:</span> {id}</div>
            {status.httpStatus !== undefined && (
              <div><span className="text-ink-3">http_status:</span> {status.httpStatus}</div>
            )}
            {status.errorCode && (
              <div><span className="text-ink-3">error_code:</span> {status.errorCode}</div>
            )}
            {status.sessionStatus && (
              <div><span className="text-ink-3">session_status:</span> {status.sessionStatus}</div>
            )}
            {status.traceId && (
              <div><span className="text-ink-3">trace_id:</span> {status.traceId}</div>
            )}
            <div><span className="text-ink-3">build:</span> {REPORT_BUILD}</div>
          </dl>
        </details>
      </div>
    );
  }

  const report = status.report;
  // (progressFlag hoisted to top of function · pre-early-returns · React #310 fix)
  // iter_7 Bug D: defensive optional chaining everywhere a BE field is read.
  // A history-click-target session may have a partial shape (e.g. older
  // schema version, synthesis fallback). Crashing kills the user journey;
  // rendering "(0)" or "—" is the right gradient.
  const ironKillCount = report.dissent?.standing_objections?.length ?? 0;
  const standingObjections = report.dissent?.standing_objections ?? [];
  const candidates = report.candidates ?? [];
  const ranking = report.ranking ?? [];
  const reportSectionCount = 5; // current_position + candidates + dissent + ranking + first_action

  // ── ★ M2 selectors (FE_SPEC §10.4-10.6) ──────────────────────────────
  // 这些 selector 返 null 时不渲染 (legacy M0 报告不显新段).
  const reportAny = report as unknown as Record<string, unknown> & {
    metadata?: { scenes_matched?: SceneSymbolId[] };
    attachment_citations?: unknown[];
  };
  const councilHealth = selectCouncilHealth(reportAny);
  const qualityCompromised = selectQualityCompromised(reportAny);
  const refund = selectRefund(reportAny);
  const scenesMatched: SceneSymbolId[] = reportAny.metadata?.scenes_matched ?? [];
  const attachmentCitationsCount = Array.isArray(reportAny.attachment_citations)
    ? reportAny.attachment_citations.length
    : 0;
  const citationsTooFew = selectAttachmentCitationsTooFew(scenesMatched, attachmentCitationsCount);

  // ── ★ M2 双下载 (§10.3): JSON download (markdown 复用 onDownloadMd) ──
  const onDownloadJson = async () => {
    if (!id) return;
    try {
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fyd-report-${id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('download json failed', err);
    }
  };

  // P0-9 free retry CTA → Dashboard with retry_of pre-filled.
  const onFreeRetry = () => {
    if (!report.session_id) return;
    navigate('/dashboard', { state: { retryOf: report.session_id } });
  };

  return (
    <div className="max-w-3xl mx-auto px-8 py-12" data-testid="report-root">
      <PageHeader
        eyebrow={t('report.eyebrow')}
        title={t('report.title')}
        actions={
          report.session_id ? (
            <Link
              to={`/sessions/${report.session_id}/live?review=1`}
              className="btn btn-secondary text-sm"
              data-testid="report-view-live"
            >
              {t('report.viewLive', { defaultValue: '查看议会过程 →' })}
            </Link>
          ) : undefined
        }
      />

      {progressFlag && me && me.councils_run <= 1 && (
        <FirstWinBanner
          councilsRun={me.councils_run}
          durationSeconds={1800}
          ironKillCount={ironKillCount}
          reportSectionCount={reportSectionCount}
          sessionId={report.session_id}
        />
      )}

      {/* ★ M2 §10.5 P0-9 · QualityCompromised banner — root field FIX-C5 */}
      {qualityCompromised && (
        <div
          className="mb-6 px-6 py-4 border border-amber-500 bg-amber-50 rounded-md"
          data-testid="report-quality-compromised-banner"
        >
          <div className="flex items-start gap-3">
            <span className="text-amber-700 font-mono text-sm">⚠</span>
            <div className="flex-1">
              <h3 className="font-serif text-base font-medium text-ink mb-2">
                {t('report.qualityCompromised.title', { defaultValue: '本议会质量受限' })}
              </h3>
              <ul className="text-sm text-ink-2 space-y-1 mb-2">
                {qualityCompromised.reasons.map((r) => (
                  <li key={r}>· {t(`report.qualityCompromised.reason.${r}`, { defaultValue: r })}</li>
                ))}
              </ul>
              {qualityCompromised.rationale && (
                <p className="text-sm text-ink-2 mb-2">{qualityCompromised.rationale}</p>
              )}
              {qualityCompromised.free_retry_eligible && (
                <button
                  type="button"
                  onClick={onFreeRetry}
                  className="btn btn-secondary text-sm"
                  data-testid="report-free-retry-cta"
                >
                  {t('report.qualityCompromised.freeRetry', { defaultValue: '免费重新议会' })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ★ M2 §10.6 P0-13 · Refund segment — root field FIX-C6 */}
      {refund && (
        <div
          className="mb-6 px-4 py-3 inline-flex items-center gap-2 border rounded-md"
          data-testid="report-refund-segment"
          data-refund-status={refund.status}
        >
          <span className="font-mono text-[10px] tracking-meta uppercase text-ink-2">
            {t('report.refund.label', { defaultValue: '退款' })}
          </span>
          <span className="text-sm">
            {t(`report.refund.${refund.status}`, {
              defaultValue:
                refund.status === 'full_refund'
                  ? '已全额退款 · ~5 分钟到账'
                  : refund.status === 'partial_refund'
                    ? '已退款 50%'
                    : refund.status === 'processing'
                      ? '退款处理中'
                      : refund.status === 'failed'
                        ? '退款失败 · 请联系支持'
                        : refund.status,
            })}
          </span>
          {refund.amount_pct > 0 && (
            <span className="font-mono text-xs text-ink-3">· {refund.amount_pct}%</span>
          )}
        </div>
      )}

      {showSubscribeNudge && <SubscribeNudge />}

      {/* §1 current_position */}
      <section className="mb-12" data-testid="report-section-current-position">
        <Eyebrow tone="azure" className="mb-3">{t('report.section.currentPosition')}</Eyebrow>
        <p className="text-ink leading-[1.8] text-[16px]">
          {report.current_position?.text ?? '—'}
        </p>
      </section>

      {/* §2 candidates */}
      <section className="space-y-6 mb-12" data-testid="report-section-candidates">
        <Eyebrow tone="gold" className="mb-3">{t('report.section.candidates')} ({candidates.length})</Eyebrow>
        {candidates.map((c) => (
          <article
            key={c.candidate_id}
            className="px-8 py-6 border border-hairline rounded-md bg-paper"
            data-testid={`report-candidate-${c.candidate_id}`}
          >
            <div className="flex items-baseline gap-4 mb-3">
              <span className="font-serif italic font-medium text-[40px] leading-none text-gold">
                {c.letter}
              </span>
              <span className="font-serif font-medium text-2xl">{c.title}</span>
            </div>
            {c.one_liner && (
              <p className="text-ink-2 text-[15px] leading-[1.7] mb-4">{c.one_liner}</p>
            )}
            {(c.pros?.length || c.cons?.length) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                {c.pros && c.pros.length > 0 && (
                  <div>
                    <div className="font-mono text-[10px] tracking-meta uppercase text-ink-2 mb-2">{t('report.pros')}</div>
                    <ul className="space-y-1">
                      {c.pros.map((p, i) => (
                        <li key={i} className="text-sm text-ink relative pl-4">
                          <span className="absolute left-0 top-[10px] w-[6px] h-px bg-jade" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {c.cons && c.cons.length > 0 && (
                  <div>
                    <div className="font-mono text-[10px] tracking-meta uppercase text-ink-2 mb-2">{t('report.cons')}</div>
                    <ul className="space-y-1">
                      {c.cons.map((p, i) => (
                        <li key={i} className="text-sm text-ink relative pl-4">
                          <span className="absolute left-0 top-[10px] w-[6px] h-px bg-vermilion" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {c.validation_steps && c.validation_steps.length > 0 && (
              <div className="mb-3">
                <div className="font-mono text-[10px] tracking-meta uppercase text-ink-2 mb-2">{t('report.validation')}</div>
                <ol className="space-y-1 list-decimal list-inside text-sm text-ink">
                  {c.validation_steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
            {c.four_week_milestone && (
              <p className="text-sm text-ink-2"><span className="font-medium">{t('report.fourWeek')}:</span> {c.four_week_milestone}</p>
            )}
            {c.kill_criteria && (
              <p className="text-sm text-vermilion mt-2"><span className="font-medium">{t('report.kill')}:</span> {c.kill_criteria}</p>
            )}
          </article>
        ))}
      </section>

      {/* §3 dissent — full standing_objections */}
      <section className="mb-12" data-testid="report-section-dissent">
        <Eyebrow tone="vermilion" className="mb-3">
          {t('report.section.dissent')} ({standingObjections.length})
          {report.dissent?.quality_compromised && (
            <span className="ml-3 text-vermilion text-xs">⚠ {t('report.qualityCompromised')}</span>
          )}
        </Eyebrow>
        {standingObjections.length === 0 ? (
          <p className="text-ink-2 text-sm">{t('report.dissentEmpty')}</p>
        ) : (
          <ul className="space-y-3">
            {standingObjections.map((o, i) => (
              <li
                key={i}
                className="px-6 py-4 border-l-4 border-vermilion bg-paper rounded-r-md"
                data-testid={`report-dissent-${i}`}
              >
                <div className="font-mono text-[10px] tracking-meta uppercase text-ink-2 mb-1">
                  {o.critic_alias} → {o.opposed_candidate_letter}
                </div>
                <p className="text-ink text-sm leading-[1.7]">"{o.quote}"</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* §4 ranking */}
      <section className="mb-12" data-testid="report-section-ranking">
        <Eyebrow tone="azure" className="mb-3">{t('report.section.ranking')}</Eyebrow>
        {ranking.length === 0 ? (
          <p className="text-ink-2 text-sm">—</p>
        ) : (
          <ol className="space-y-2">
            {ranking.map((r) => {
              const label = r.candidate_letter ?? r.candidate_id ?? '?';
              const reason = r.reason ?? r.rationale ?? '';
              return (
                <li key={`${r.rank}-${label}`} className="flex gap-4 px-6 py-3 bg-paper border border-hairline rounded-md">
                  <span className="font-serif italic text-[24px] leading-none text-gold">#{r.rank}</span>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{label}</div>
                    <div className="text-ink-2 text-sm">{reason}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* §5 first_action */}
      <section className="mb-12" data-testid="report-section-first-action">
        <Eyebrow tone="jade" className="mb-3">{t('report.section.firstAction')}</Eyebrow>
        <div className="px-8 py-6 border border-jade bg-jade-100/40 rounded-md">
          <p className="font-serif font-medium text-xl text-jade-700 mb-2 whitespace-pre-wrap">
            {report.first_action?.text ?? report.first_action?.one_liner ?? '—'}
          </p>
          {report.first_action?.success_criterion && (
            <p className="text-sm text-ink-2">
              <span className="font-medium">{t('report.successCriterion')}:</span> {report.first_action.success_criterion}
            </p>
          )}
          {report.first_action?.deadline && (
            <p className="text-sm text-ink-2">
              <span className="font-medium">{t('report.deadline')}:</span> {report.first_action.deadline}
            </p>
          )}
        </div>
      </section>

      {/* ★ M2 §10.4 council_health 透明度模块 */}
      {councilHealth && (
        <section className="mb-12" data-testid="report-section-council-health">
          <Eyebrow tone="ink" className="mb-3">
            {t('report.section.council_health', { defaultValue: '议会健康度' })}
          </Eyebrow>
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="px-3 py-2 border border-hairline rounded">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.iron_kills_count', { defaultValue: 'iron 砍候选' })}
              </dt>
              <dd className="text-ink">{councilHealth.iron_kills_count}</dd>
            </div>
            <div className="px-3 py-2 border border-hairline rounded">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.v9_warnings', { defaultValue: 'v9 警告' })}
              </dt>
              <dd className="text-ink">{councilHealth.v9_warnings}</dd>
            </div>
            <div className="px-3 py-2 border border-hairline rounded">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.fallback_used', { defaultValue: 'fallback 使用' })}
              </dt>
              <dd className={councilHealth.fallback_used ? 'text-vermilion-700' : 'text-ink'}>
                {councilHealth.fallback_used ? t('common.yes', { defaultValue: '是' }) : t('common.no', { defaultValue: '否' })}
              </dd>
            </div>
            <div className="px-3 py-2 border border-hairline rounded">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.gating_user_advances', { defaultValue: '用户继续' })}
              </dt>
              <dd className="text-ink">{councilHealth.gating_user_advances}</dd>
            </div>
            <div className="px-3 py-2 border border-hairline rounded">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.phase_count', { defaultValue: 'phase 数' })}
              </dt>
              <dd className="text-ink">{councilHealth.phase_count}</dd>
            </div>
            <div className="px-3 py-2 border border-hairline rounded">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.role_distinct_count', { defaultValue: '角色去重' })}
              </dt>
              <dd className="text-ink">{councilHealth.role_distinct_count}</dd>
            </div>
            <div className="px-3 py-2 border border-hairline rounded col-span-2 md:col-span-1">
              <dt className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t('report.health.total_llm_calls', { defaultValue: 'LLM 调用' })}
              </dt>
              <dd className="text-ink">{councilHealth.total_llm_calls}</dd>
            </div>
          </dl>
        </section>
      )}

      {/* ★ M2 §10.7 P0-5 · attachment_citations tooFew chip */}
      {citationsTooFew && (
        <div
          className="mb-6 px-4 py-2 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 rounded-md text-sm"
          data-testid="report-citations-too-few"
        >
          <span className="text-amber-700">⚠</span>
          <span>{t('report.citations.tooFew', { defaultValue: '引用不足 3 条 · 议会质量受限' })}</span>
        </div>
      )}

      {/* audit trailer */}
      {report.audit && (
        <section className="mb-12" data-testid="report-section-audit">
          <Eyebrow tone="ink" className="mb-3">{t('report.section.audit')}</Eyebrow>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <AuditCheck
              label={t('report.audit.rule3')}
              ok={report.audit.rule_3_satisfied}
            />
            <AuditCheck
              label={t('report.audit.candidatesRange')}
              ok={report.audit.candidates_count_in_range}
            />
            <AuditCheck
              label={t('report.audit.firstActionSingle')}
              ok={report.audit.first_action_is_single}
            />
            <AuditCheck
              label={t('report.audit.noForbidden')}
              ok={!report.audit.forbidden_phrases_found || report.audit.forbidden_phrases_found.length === 0}
            />
          </dl>
          {(report.audit?.forbidden_phrases_found?.length ?? 0) > 0 && (
            <p className="mt-3 text-xs text-vermilion-700">
              {t('report.audit.forbiddenList')}: {(report.audit?.forbidden_phrases_found ?? []).join(', ')}
            </p>
          )}
          <p className="mt-3 font-mono text-[10px] text-ink-3">
            session_id={report.session_id} · generated={report.generated_at?.slice(0, 19).replace('T', ' ')}
          </p>
        </section>
      )}

      {/* Post-report continuation CTAs (UX_DESIGN §6A). */}
      <div className="border-t border-hairline pt-8 pb-4">
        <h3 className="font-mono text-xs text-ink-3 tracking-meta uppercase mb-4">{t('report.continuation')}</h3>
        {progressFlag && me && me.councils_run <= 1 ? (
          <div className="space-y-3">
            <NextActionCta
              onPrimary={() => navigate('/dashboard')}
              onSecondary={onDownloadMd}
            />
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('report.cta.backHome')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              {t('report.cta.startNew')}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/dashboard')}>
              {t('report.cta.backHome')}
            </Button>
            <Button variant="ghost" onClick={onDownloadMd}>
              {t('report.cta.downloadMd')}
            </Button>
            {/* ★ M2 §10.3 双下载: JSON */}
            <Button variant="ghost" onClick={onDownloadJson} data-testid="report-download-json">
              {t('report.cta.downloadJson', { defaultValue: '下载 JSON' })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
